import { z } from 'zod';
import { appointmentReasonSchema } from '../planning/appointment.js';
import { uuidV7Schema } from '../uuid.js';
/**
 * L'**activité récente** du tableau de bord (R-B1) — la carte « Activité récente » du prototype
 * (`ghp-bo/screens.js`, `recent`).
 *
 * ⚠️ **Aucune table d'événements, et c'est la décision** : le fil se reconstruit à la lecture
 * depuis les horodatages **déjà stockés** par les gestes qu'il raconte. Un journal à part dirait
 * deux fois la même chose, et la première divergence — un événement écrit, le geste annulé — ne
 * se verrait nulle part. Conséquence : seul entre ici ce qu'une colonne date **de façon fiable**.
 *
 * Les quatre genres du prototype, et d'où vient leur date :
 *
 * - `inspection_submitted` — « Équipe X a terminé le contrôle » : la **réception** de la
 *   proposition par le serveur (`Proposal.created_at`). ⚠️ Pas `submitted_at`, l'heure de clôture
 *   que déclare la tablette : une transmission restée des heures dans l'outbox arriverait alors
 *   **sous** des événements plus récents, et le bureau ne la verrait jamais passer en tête ;
 * - `appointment_scheduled` — « Contrôle planifié » : la pose du créneau (`Appointment.created_at`),
 *   le créneau lui-même dans `detail`. Un créneau annulé depuis est rendu **comme son
 *   annulation** : daté et rangé à `Appointment.cancelled_at`, qui voyage aussi dans `detail` —
 *   l'écran le titre « Rendez-vous annulé », et une annulation du jour remonte en tête.
 *   ⚠️ **Un déplacement est une seule ligne** : le nouveau créneau porte celui qu'il remplace
 *   (`movedFrom`, lu par `Appointment.replaces_id`), et l'ancien, annulé par ce remplacement, ne
 *   sort pas une seconde fois. `fromField` dit qu'il a été posé **depuis la tablette** — c'est ce
 *   qui prévient le bureau d'un rendez-vous déplacé sur le terrain (demande de GHP, 08/10/2026) ;
 * - `list_imported` — « Import groupé » : l'import d'un fichier (`InquiryImport.created_at`) ;
 * - `inquiry_validated` — « Contrôle validé » : la décision (`ValidationDecision.created_at`,
 *   `decision = 'validated'`). Un renvoi au terrain n'est pas une validation, il n'entre pas.
 */
export const DASHBOARD_ACTIVITY_KINDS = [
    'inspection_submitted',
    'appointment_scheduled',
    'list_imported',
    'inquiry_validated',
];
export const dashboardActivityKindSchema = z.enum(DASHBOARD_ACTIVITY_KINDS);
/** Ce que la carte montre par défaut — les quatre à huit lignes du prototype. */
export const DEFAULT_DASHBOARD_ACTIVITY_LIMIT = 8;
/** Au-delà, ce n'est plus une carte, c'est un journal — qui aura sa propre page. */
export const MAX_DASHBOARD_ACTIVITY_LIMIT = 50;
/**
 * L'adresse telle que la ligne l'imprime — « 14 Rue de la Minais · Ste-Luce ». `street` porte le
 * numéro et la voie **déjà joints**, `null` quand la demande n'a ni l'un ni l'autre ; la commune,
 * elle, est toujours connue.
 */
export const dashboardActivityAddressSchema = z.object({
    street: z.string().nullable(),
    city: z.string(),
    /** La commune déléguée, `null` sans elle — « Montjean-sur-Loire » plutôt que la commune nouvelle. */
    delegatedName: z.string().nullable(),
});
const activityBase = {
    /**
     * L'événement : la ligne qui le date (proposition, rendez-vous, import, décision). Genre, instant
     * et demande ne suffisent pas à le nommer — une demande peut porter deux créneaux datés à
     * l'identique (M24).
     */
    id: uuidV7Schema,
    /** Quand c'est arrivé — la colonne qui date le geste, cf. l'en-tête. */
    at: z.iso.datetime({ offset: true }),
    /** La demande concernée, pour ouvrir sa fiche — `null` pour un import, qui en porte plusieurs. */
    inquiryId: uuidV7Schema.nullable(),
    /** La « Réf. GHP » de la demande, `null` pour un import. */
    reference: z.string().nullable(),
    address: dashboardActivityAddressSchema.nullable(),
    /** L'équipe du geste : celle qui a déposé, ou celle de la demande. `null` si aucune. */
    teamName: z.string().nullable(),
};
export const dashboardActivityItemSchema = z.discriminatedUnion('kind', [
    z.object({ kind: z.literal('inspection_submitted'), ...activityBase, detail: z.null() }),
    z.object({
        kind: z.literal('appointment_scheduled'),
        ...activityBase,
        detail: z.object({
            /** Le créneau posé — « 03/11 » au prototype. */
            scheduledAt: z.iso.datetime({ offset: true }),
            /** Annulé depuis, ou `null` : l'événement reste, l'écran dit qu'il n'a plus cours. */
            cancelledAt: z.iso.datetime({ offset: true }).nullable(),
            /** Posé depuis la tablette par l'équipe, plutôt qu'au bureau. */
            fromField: z.boolean(),
            /** Le créneau que celui-ci remplace — un déplacement —, `null` pour une première pose. */
            movedFrom: z.iso.datetime({ offset: true }).nullable(),
            /** Le motif du déplacement (M24), `null` sans lui. */
            reason: appointmentReasonSchema.nullable(),
        }),
    }),
    z.object({
        kind: z.literal('list_imported'),
        ...activityBase,
        detail: z.object({
            fileName: z.string(),
            /**
             * Les demandes que le fichier a écrites **sur le marché demandé** — toutes, sous « Tous les
             * marchés ». Un doublon compte : il s'est écrit (M21).
             */
            inquiryCount: z.number().int().min(0),
        }),
    }),
    z.object({ kind: z.literal('inquiry_validated'), ...activityBase, detail: z.null() }),
]);
/**
 * Le fil, **du plus récent au plus ancien**, filtré par marché comme les compteurs de statut :
 * un événement appartient au marché de la commune de sa demande (M31). Un import y figure dès
 * qu'une de ses demandes y tombe.
 */
export const dashboardActivitySchema = z.object({
    items: z.array(dashboardActivityItemSchema),
});
//# sourceMappingURL=activity.js.map